import { DatePicker, type DatePickerProps } from '@mui/x-date-pickers/DatePicker';
import { forwardRef } from 'react';
import { LabeledField } from '../LabeledField';
import { Calendar } from '../../../foundations/icons';

/**
 * @tier molecule
 *
 * Single-day picker with a stacked label. Built on
 * `@mui/x-date-pickers`. The field uses the calendar adornment styling
 * established by the legacy `VyDatePicker` in `apps/app/src/components/
 * new-design/date-picker/` (the one used inside the `VyTableFilters`
 * popover) — see `VyTableFilters` for the canonical visual reference.
 *
 * Requires a `LocalizationProvider dateAdapter={AdapterDayjs}` ancestor —
 * `apps/app` provides this at root; the lib's Storybook adds one via the
 * preview decorator.
 */
export type VyDatePickerProps = Omit<DatePickerProps, 'slotProps' | 'label'> & {
  /** Field label rendered above the picker. */
  label?: string;
  /** Hint shown below the input. Becomes the error message when `error` is true. */
  helperText?: string;
  /** Renders the input in the error state. */
  error?: boolean;
  /** Shows an inline clear (✕) button in the field when a date is selected. */
  clearable?: boolean;
};

const OpenPickerIcon = () => <Calendar width={20} height={20} />;

export const VyDatePicker = forwardRef<HTMLDivElement, VyDatePickerProps>(
  function VyDatePicker({ label, helperText, error, clearable, ...rest }, ref) {
    return (
      <LabeledField label={label} helperText={helperText} error={error}>
        <DatePicker
          ref={ref}
          {...rest}
          slots={{ openPickerIcon: OpenPickerIcon }}
          slotProps={{
            textField: {
              fullWidth: true,
              size: 'small',
              error,
            },
            field: { clearable },
          }}
        />
      </LabeledField>
    );
  },
);
